import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoneyShort, fmtMoney, fmtDate, fmtDateTime, timeAgo } from "@/lib/caesar";
import { useAuth } from "@/context/AuthContext";
import { MetricCard, SectionLabel, Loading } from "@/components/common";
import { CaesarFive, ListCard, MutedEmpty } from "@/components/widgets";
import CommandStrip from "@/components/CommandStrip";
import { Pencil, DollarSign, TrendingUp, Users, FolderKanban, CalendarClock, Heart, Target, AlertTriangle, Check, Brain } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

export default function WarRoom() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [projectMap, setProjectMap] = useState({});
  const [projects, setProjects] = useState([]);
  const [chief, setChief] = useState(null);
  const [editMission, setEditMission] = useState(false);
  const [missionText, setMissionText] = useState("");
  const [now] = useState(new Date());

  const load = useCallback(async () => {
    const [wr, projs] = await Promise.all([api.get("/dashboard/war-room"), api.get("/projects")]);
    setData(wr.data);
    setMissionText(wr.data.mission || "");
    const map = {}; projs.data.forEach((p) => (map[p.id] = p.name));
    setProjectMap(map);
    setProjects(projs.data);
    api.get("/chief/overview").then(({ data }) => setChief(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveMission = async () => {
    await api.put("/mission", { text: missionText });
    setEditMission(false);
    toast.success("Mission updated");
    load();
  };

  if (!data) return <Loading label="Assembling the War Room…" />;
  const se = data.state_of_empire;
  const greeting = now.getHours() < 12 ? "Good Morning" : now.getHours() < 18 ? "Good Afternoon" : "Good Evening";
  const momentum = projects.filter((p) => ["green", "yellow", "red"].includes(p.status) && (p.progress || 0) > 0).sort((a, b) => (b.progress || 0) - (a.progress || 0)).slice(0, 6);

  return (
    <div className="space-y-8">
      <div>
        <p className="label-caps text-gold">Command Center</p>
        <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight mt-1" data-testid="war-room-greeting">
          {greeting}, {user?.role === "super_admin" ? "Commander" : user?.name?.split(" ")[0]}
        </h1>
        <p className="text-[#A3A3A3] mt-2 font-num text-sm">
          {now.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })} · {now.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
        </p>
      </div>

      {/* Today's Mission */}
      <div className="bg-surface border border-gold/30 rounded-xl p-5 sm:p-6 relative" data-testid="todays-mission">
        <div className="flex items-center justify-between">
          <span className="label-caps text-gold">Today's Mission</span>
          <button data-testid="mission-edit-btn" onClick={() => setEditMission(true)} className="text-[#A3A3A3] hover:text-gold transition-colors"><Pencil size={15} /></button>
        </div>
        <p className="mt-3 text-lg sm:text-2xl font-display font-semibold leading-snug">
          {data.mission || "Set today's mission. Close revenue. Advance projects. Serve customers. Strengthen relationships."}
        </p>
      </div>

      {/* Command metrics strip */}
      <CommandStrip />

      {/* AI Chief of Staff preview */}
      <button
        data-testid="chief-preview-card"
        onClick={() => navigate("/chief")}
        className="w-full text-left bg-surface border border-gold/30 rounded-xl p-5 sm:p-6 hover:border-gold/60 hover:bg-surface2 transition-colors"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Brain size={18} className="text-gold" />
            <span className="label-caps text-gold">SZR AI Chief of Staff</span>
          </div>
          <span className="text-xs text-[#A3A3A3]">Open →</span>
        </div>
        {chief && chief.top5.length > 0 ? (
          <div className="mt-3 space-y-1.5">
            {chief.top5.slice(0, 3).map((c, i) => (
              <div key={c.id} className="flex items-center gap-3 text-sm">
                <span className="font-display font-extrabold text-gold w-4">{i + 1}</span>
                <span className="truncate flex-1">{c.title}</span>
                {c.value > 0 && <span className="font-num text-sgreen shrink-0">{fmtMoneyShort(c.value)}</span>}
              </div>
            ))}
            <p className="text-xs text-[#A3A3A3] pt-1">{chief.signal_count} priority signal{chief.signal_count === 1 ? "" : "s"} · tap for full brief, decisions & Ask SZR</p>
          </div>
        ) : (
          <p className="text-sm text-[#A3A3A3] mt-3">Your AI Chief of Staff surfaces the top moves, decisions and follow-ups — grounded in live SZR data.</p>
        )}
      </button>

      {/* Caesar's Five */}
      <div>
        <SectionLabel>The Five — the five moves that matter now</SectionLabel>
        <CaesarFive items={data.caesars_five} projectMap={projectMap} onReload={load} />
      </div>

      {/* State of the Empire */}
      <div>
        <SectionLabel>State of the Empire</SectionLabel>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <MetricCard testId="soe-money" label="Money" icon={DollarSign} value={fmtMoneyShort(se.money.collected_month)} sub={`${fmtMoneyShort(se.money.overdue)} overdue · ${fmtMoneyShort(se.money.expected)} expected`} accent={se.money.overdue > 0 ? "#F59E0B" : "#10B981"} onClick={() => navigate("/money")} />
          <MetricCard testId="soe-pipeline" label="Pipeline" icon={TrendingUp} value={fmtMoneyShort(se.pipeline)} sub="active opportunities" onClick={() => navigate("/money")} />
          <MetricCard testId="soe-customers" label="Customers" icon={Users} value={se.customers} sub="need attention" onClick={() => navigate("/people")} />
          <MetricCard testId="soe-projects" label="Projects" icon={FolderKanban} value={se.projects.active} sub={`${se.projects.yellow} yellow · ${se.projects.red} red`} accent={se.projects.red > 0 ? "#E11D48" : "#FAFAFA"} onClick={() => navigate("/projects")} />
          <MetricCard testId="soe-deadlines" label="Deadlines" icon={CalendarClock} value={se.deadlines} sub="within 7 days" onClick={() => navigate("/today")} />
          <MetricCard testId="soe-relationships" label="Relationships" icon={Heart} value={se.relationships} sub="follow-ups due" onClick={() => navigate("/people")} />
          <MetricCard testId="soe-opportunities" label="Opportunities" icon={Target} value={se.opportunities} sub="in motion" onClick={() => navigate("/people")} />
          <MetricCard testId="soe-alerts" label="Red Alerts" icon={AlertTriangle} value={se.red_alerts} sub="critical issues" accent={se.red_alerts > 0 ? "#E11D48" : "#10B981"} onClick={() => navigate("/alerts")} />
        </div>
      </div>

      {/* Secondary grid */}
      <div className="grid md:grid-cols-2 gap-4">
        <ListCard title="Next 24 Hours" count={data.next_24h.length} testId="next-24h">
          {data.next_24h.length === 0 ? <MutedEmpty>Nothing scheduled in the next 24 hours.</MutedEmpty> :
            data.next_24h.map((x, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-2.5 hover:bg-surface2 rounded-lg">
                <div className="min-w-0"><p className="text-sm truncate">{x.title}</p><span className="label-caps text-[0.55rem]">{x.type}</span></div>
                <span className="font-num text-xs text-[#A3A3A3] shrink-0 ml-2">{fmtDateTime(x.when)}</span>
              </div>
            ))}
        </ListCard>

        <ListCard title="Money Closeable Now" count={data.money_closeable.length} testId="money-closeable">
          {data.money_closeable.length === 0 ? <MutedEmpty>No money ready to close.</MutedEmpty> :
            data.money_closeable.map((m) => (
              <div key={m.id} className="flex items-center justify-between px-3 py-2.5 hover:bg-surface2 rounded-lg cursor-pointer" onClick={() => navigate("/money")}>
                <div className="min-w-0"><p className="text-sm truncate">{m.party}</p><span className="label-caps text-[0.55rem]">{m.status}</span></div>
                <span className="font-num text-sm text-sgreen shrink-0 ml-2">{fmtMoney(m.amount)}</span>
              </div>
            ))}
        </ListCard>

        <ListCard title="Waiting On" count={data.waiting_on.length} testId="waiting-on">
          {data.waiting_on.length === 0 ? <MutedEmpty>Not waiting on anyone.</MutedEmpty> :
            data.waiting_on.map((t) => (
              <div key={t.id} className="px-3 py-2.5 hover:bg-surface2 rounded-lg"><p className="text-sm">{t.title}</p><span className="label-caps text-[0.55rem]">{t.owner || "—"}</span></div>
            ))}
        </ListCard>

        {user?.role === "super_admin" && (
          <ListCard title="Needs Toriano" count={data.needs_toriano.length} testId="needs-toriano">
            {data.needs_toriano.length === 0 ? <MutedEmpty>Nothing escalated.</MutedEmpty> :
              data.needs_toriano.map((e) => (
                <div key={e.id} className="px-3 py-2.5 hover:bg-surface2 rounded-lg flex items-center justify-between gap-2">
                  <div className="min-w-0"><p className="text-sm truncate">{e.title}</p><span className="label-caps text-[0.55rem] text-sred">{e.reason}</span></div>
                  <button data-testid={`escalation-resolve-${e.id}`} onClick={async () => { await api.put(`/escalations/${e.id}`, { status: "resolved" }); toast.success("Resolved"); load(); }} className="text-sgreen shrink-0"><Check size={16} /></button>
                </div>
              ))}
          </ListCard>
        )}

        <ListCard title="Project Momentum" count={momentum.length} testId="project-momentum">
          {momentum.length === 0 ? <MutedEmpty>Set a progress % on projects to track momentum.</MutedEmpty> :
            momentum.map((p) => (
              <div key={p.id} className="px-3 py-2.5">
                <div className="flex justify-between text-sm mb-1"><span className="truncate mr-2">{p.name}</span><span className="font-num text-gold">{p.progress}%</span></div>
                <div className="h-2 rounded-full bg-edge overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.min(100, p.progress)}%`, background: "#D4AF37" }} /></div>
              </div>
            ))}
        </ListCard>

        <ListCard title="Recent Wins" count={data.recent_wins.length} testId="recent-wins">
          {data.recent_wins.length === 0 ? <MutedEmpty>No wins recorded yet.</MutedEmpty> :
            data.recent_wins.map((w) => (
              <div key={w.id} className="px-3 py-2.5 flex items-center justify-between"><p className="text-sm text-[#FAFAFA]">{w.description}</p><span className="font-num text-[0.65rem] text-[#5a5a5a] shrink-0 ml-2">{timeAgo(w.created_at)}</span></div>
            ))}
        </ListCard>
      </div>

      <Dialog open={editMission} onOpenChange={setEditMission}>
        <DialogContent className="bg-surface border-edge">
          <DialogHeader><DialogTitle className="font-display text-2xl">Today's Mission</DialogTitle></DialogHeader>
          <Textarea data-testid="mission-textarea" value={missionText} onChange={(e) => setMissionText(e.target.value)} className="bg-ink border-edge min-h-[120px]" placeholder="What matters most today?" />
          <button data-testid="mission-save" onClick={saveMission} className="h-11 rounded-lg bg-gold text-ink font-bold hover:bg-[#e6c14e] transition-colors">Save Mission</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
